/**
 * The outside world: a language model and a web search, both bring-your-own-key.
 *
 * The wire shapes are carried over from the airfield demo's own provider panel
 * rather than reinvented, including the reason it has four of them: they are
 * genuinely different. Anthropic puts the system prompt in its own field and
 * insists on max_tokens, Google puts the key in the query string and wraps
 * everything in parts, and pretending one shape fits all is how "connect a
 * model" quietly comes to mean "connect OpenAI".
 *
 * Nothing here is required. With no key configured the assistant still works on
 * the platform's own records — she simply says that the outside is not
 * connected, rather than implying she looked and found nothing. The difference
 * matters: "I have no model connected" and "I searched and there is nothing"
 * are opposite claims, and only one of them is true.
 *
 * A note on where this runs. Called from a browser, these endpoints are subject
 * to that page's network policy. In local development they work. On a published
 * artifact the host's policy blocks every origin it does not allowlist, so the
 * call fails at the network layer — which is reported as what it is, a blocked
 * request, not as an empty answer.
 */

export type ModelShape = "openai" | "anthropic" | "google" | "platform";

export interface ModelProvider {
  id: string;
  label: string;
  endpoint: string;
  model: string;
  keyLabel: string;
  shape: ModelShape;
  hint: string;
}

export const MODEL_PROVIDERS: readonly ModelProvider[] = [
  {
    /*
     * The deployment's own model, and the right answer wherever there is a
     * deployment. Every other entry here needs a key in this browser: the
     * operator's key, billed to them, readable by anybody who can open the
     * browser, and obtained separately by each person who wants an
     * assistant. This one asks the platform to compose, so the key stays on
     * the server, one key serves everybody, the spend has a ceiling an
     * administrator sets, and the call is made from somewhere whose network
     * policy allows it.
     *
     * It is listed first and chosen by default. It reports itself
     * unavailable — rather than failing — when the app is on sample data or
     * the deployment holds no key, which is when the entries below matter.
     */
    id: "platform",
    label: "The platform's model",
    endpoint: "",
    model: "",
    keyLabel: "",
    shape: "platform",
    hint:
      "No key here. The deployment holds one and composes on your behalf, so nothing is sent " +
      "from this browser to a model and the spend is capped on the server.",
  },
  {
    id: "anthropic",
    label: "Claude · Anthropic",
    endpoint: "https://api.anthropic.com/v1/messages",
    model: "claude-sonnet-5",
    keyLabel: "Anthropic API key",
    shape: "anthropic",
    hint:
      "Keys are made at console.anthropic.com. Calling it straight from a page needs the " +
      "direct-browser-access header, which this sends. Check the model name in the console — " +
      "a wrong one returns a 404, not a wrong answer.",
  },
  {
    id: "openai",
    label: "ChatGPT · OpenAI",
    endpoint: "https://api.openai.com/v1/chat/completions",
    model: "gpt-4o-mini",
    keyLabel: "OpenAI API key",
    shape: "openai",
    hint:
      "Any OpenAI-compatible gateway works here too — Azure OpenAI, OpenRouter, Together, Groq — " +
      "give it that gateway's URL.",
  },
  {
    id: "google",
    label: "Gemini · Google",
    endpoint: "https://generativelanguage.googleapis.com/v1beta/models",
    model: "gemini-2.0-flash",
    keyLabel: "Google AI Studio key",
    shape: "google",
    hint: "The model name goes in the URL, so it has to be one Google currently serves.",
  },
  {
    id: "ollama",
    label: "On this machine · Ollama or LM Studio",
    endpoint: "http://localhost:11434/v1/chat/completions",
    model: "llama3.1",
    keyLabel: "Key, if your server wants one",
    shape: "openai",
    hint:
      "Nothing leaves the machine. Ollama must be started with OLLAMA_ORIGINS=* or the browser " +
      "refuses the call — and that refusal looks exactly like the model being down.",
  },
  {
    id: "custom",
    label: "Something else, OpenAI-compatible",
    endpoint: "",
    model: "",
    keyLabel: "Key, if the endpoint needs one",
    shape: "openai",
    hint: "Anything that speaks /v1/chat/completions.",
  },
];

export interface SearchProvider {
  id: string;
  label: string;
  keyLabel: string;
  hint: string;
}

export const SEARCH_PROVIDERS: readonly SearchProvider[] = [
  { id: "serper", label: "Serper", keyLabel: "serper.dev key", hint: "Google results over a plain API key." },
  { id: "tavily", label: "Tavily", keyLabel: "Tavily key", hint: "Search built for retrieval rather than for people." },
  {
    id: "brave",
    label: "Brave",
    keyLabel: "Brave Search key",
    hint: "Some plans refuse browser-origin calls; that failure arrives as a CORS error.",
  },
];

export interface ExternalConfig {
  modelProvider: string;
  endpoint: string;
  model: string;
  modelKey: string;
  searchProvider: string;
  searchKey: string;
}

export const EMPTY_CONFIG: ExternalConfig = {
  modelProvider: "platform",
  endpoint: "",
  model: "",
  modelKey: "",
  searchProvider: "serper",
  searchKey: "",
};

const STORE_KEY = "skylogics.assistant.external";

/**
 * The key lives in this browser and nowhere else — same honesty as the sign-in
 * gate, which says plainly that a client-side store stops someone using the
 * screen and does not protect the data.
 */
export function loadConfig(): ExternalConfig {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { ...EMPTY_CONFIG };
    return { ...EMPTY_CONFIG, ...(JSON.parse(raw) as Partial<ExternalConfig>) };
  } catch {
    return { ...EMPTY_CONFIG };
  }
}

export function saveConfig(cfg: ExternalConfig): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(cfg));
  } catch {
    /* a browser that refuses storage still gets a working session, just not a remembered one */
  }
}

/**
 * What the deployment can compose with, registered at start-up.
 *
 * A module-level registration rather than a prop, because `askModel` is
 * called from a dozen screens that hold a config and no data source, and
 * threading an adapter through all of them to reach one endpoint would be a
 * larger change than the thing it carries. Null means there is no platform
 * to ask — sample data, or a deployment that holds no key — and `hasModel`
 * says so rather than letting a call fail at the network.
 */
export interface PlatformModel {
  /** What the deployment holds, for showing in Settings and citing in an answer. */
  model: string;
  compose(brief: string, material: string, signal?: AbortSignal): Promise<ModelReply>;
}

let platform: PlatformModel | null = null;

export function usePlatformModel(m: PlatformModel | null): void {
  platform = m;
}

export function platformModel(): PlatformModel | null {
  return platform;
}

export function providerOf(id: string): ModelProvider {
  return MODEL_PROVIDERS.find((p) => p.id === id) ?? MODEL_PROVIDERS[0];
}

export const hasModel = (c: ExternalConfig): boolean =>
  c.modelProvider === "platform"
    ? platform !== null
    : Boolean((c.modelKey || c.modelProvider === "ollama") && (c.endpoint || providerOf(c.modelProvider).endpoint));

export const hasSearch = (c: ExternalConfig): boolean => Boolean(c.searchKey);

export class ExternalUnavailable extends Error {}

/** Turns the several ways a browser fetch can fail into one sentence an operator can act on. */
function explain(e: unknown, what: string): never {
  const msg = e instanceof Error ? e.message : String(e);
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) {
    throw new ExternalUnavailable(
      `The ${what} call did not leave the browser. That is a network or policy refusal — ` +
        `on a published page the host blocks origins it has not allowlisted, and locally it usually means CORS.`,
    );
  }
  throw new ExternalUnavailable(`The ${what} call failed: ${msg}`);
}

export interface ModelReply {
  text: string;
  model: string;
}

/** A turn of the conversation so far, oldest first. */
export interface HistoryTurn {
  role: "user" | "assistant";
  content: string;
}

/**
 * The conversation as a provider will take it: starting with the person,
 * alternating, and never two turns from the same side in a row — Anthropic
 * refuses a list that breaks either rule, so consecutive turns are joined
 * rather than sent as they are.
 */
export function shapeHistory(history: readonly HistoryTurn[], question: string): HistoryTurn[] {
  const out: HistoryTurn[] = [];
  for (const h of [...history, { role: "user" as const, content: question }]) {
    const content = h.content.trim();
    if (!content) continue;
    if (!out.length && h.role !== "user") continue;
    const last = out[out.length - 1];
    if (last && last.role === h.role) last.content = `${last.content}\n\n${content}`;
    else out.push({ role: h.role, content });
  }
  return out;
}

/**
 * Every text block of an Anthropic reply, joined. A newer model can answer
 * with a block that is not text ahead of the text — reading only the first
 * block then returns an empty answer from a call that succeeded, and the
 * assistant falls back as if no model were connected.
 */
export function anthropicText(j: { content?: { type?: string; text?: string }[] }): string {
  return (j.content ?? [])
    .filter((b) => b.type === "text" && typeof b.text === "string")
    .map((b) => b.text)
    .join("")
    .trim();
}

export async function askModel(
  cfg: ExternalConfig,
  system: string,
  question: string,
  signal?: AbortSignal,
  /** What was said before this question, so a reply can follow the conversation. */
  history: readonly HistoryTurn[] = [],
): Promise<ModelReply> {
  const p = providerOf(cfg.modelProvider);
  if (p.shape === "platform") {
    if (!platform)
      throw new ExternalUnavailable(
        "This deployment has no model of its own. Settings \u203a Sky can point at one with a key of your own.",
      );
    /* The charter the platform prepends is not a copy of `system`: the
       platform's own is the one that cannot be turned off, and this is the
       screen's brief on top of it. */
    const said = history.length
      ? `Conversation so far:\n${history.map((h) => `${h.role === "user" ? "Operator" : "Sky"}: ${h.content}`).join("\n")}\n\n`
      : "";
    return platform.compose(system, said + question, signal);
  }
  const endpoint = cfg.endpoint || p.endpoint;
  const model = cfg.model || p.model;
  if (!endpoint) throw new ExternalUnavailable("No endpoint is set for this provider.");

  try {
    if (p.shape === "anthropic") {
      const r = await fetch(endpoint, {
        method: "POST",
        signal,
        headers: {
          "content-type": "application/json",
          "x-api-key": cfg.modelKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model,
          max_tokens: 1024,
          system,
          messages: shapeHistory(history, question),
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${await r.text().catch(() => "")}`.slice(0, 300));
      const j = await r.json();
      return { text: anthropicText(j), model };
    }

    if (p.shape === "google") {
      const url = `${endpoint}/${model}:generateContent?key=${encodeURIComponent(cfg.modelKey)}`;
      const r = await fetch(url, {
        method: "POST",
        signal,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          systemInstruction: { parts: [{ text: system }] },
          contents: shapeHistory(history, question).map((h) => ({
            role: h.role === "assistant" ? "model" : "user",
            parts: [{ text: h.content }],
          })),
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${await r.text().catch(() => "")}`.slice(0, 300));
      const j = await r.json();
      return { text: (j.candidates?.[0]?.content?.parts?.[0]?.text ?? "").trim(), model };
    }

    const r = await fetch(endpoint, {
      method: "POST",
      signal,
      headers: {
        "content-type": "application/json",
        ...(cfg.modelKey ? { authorization: `Bearer ${cfg.modelKey}` } : {}),
      },
      body: JSON.stringify({
        model,
        messages: [{ role: "system", content: system }, ...shapeHistory(history, question)],
      }),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text().catch(() => "")}`.slice(0, 300));
    const j = await r.json();
    return { text: (j.choices?.[0]?.message?.content ?? "").trim(), model };
  } catch (e) {
    explain(e, "model");
  }
}

export interface SearchHit {
  title: string;
  url: string;
  snippet?: string;
}

export async function searchWeb(cfg: ExternalConfig, query: string, signal?: AbortSignal): Promise<SearchHit[]> {
  const n = 6;
  try {
    if (cfg.searchProvider === "serper") {
      const r = await fetch("https://google.serper.dev/search", {
        method: "POST",
        signal,
        headers: { "content-type": "application/json", "X-API-KEY": cfg.searchKey },
        body: JSON.stringify({ q: query, num: n }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json();
      return (j.organic ?? []).slice(0, n).map((h: Record<string, string>) => ({
        title: h.title,
        url: h.link,
        snippet: h.snippet,
      }));
    }
    if (cfg.searchProvider === "tavily") {
      const r = await fetch("https://api.tavily.com/search", {
        method: "POST",
        signal,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ api_key: cfg.searchKey, query, max_results: n }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json();
      return (j.results ?? []).slice(0, n).map((h: Record<string, string>) => ({
        title: h.title,
        url: h.url,
        snippet: h.content,
      }));
    }
    const r = await fetch(`https://api.search.brave.com/res/v1/web/search?count=${n}&q=${encodeURIComponent(query)}`, {
      signal,
      headers: { accept: "application/json", "X-Subscription-Token": cfg.searchKey },
    });
    if (!r.ok) throw new Error(`${r.status}`);
    const j = await r.json();
    return (j.web?.results ?? []).slice(0, n).map((h: Record<string, string>) => ({
      title: h.title,
      url: h.url,
      snippet: h.description,
    }));
  } catch (e) {
    explain(e, "search");
  }
}
